import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Search, Compass, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import LeaderboardRow from "@/components/LeaderboardRow";
import { FadeUp } from "@/components/Reveal";

const CATEGORIES = ["AI", "SEO", "Travel", "Social", "Real Estate", "Marketing", "Others"];
const REGIONS = ["US", "UK", "India", "Global"];
const REGION_SLUGS = { us: "US", uk: "UK", india: "India", global: "Global" };
const REGION_LABELS = { US: "United States", UK: "United Kingdom", India: "India", Global: "Global" };

function Pill({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap font-mono2 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] rounded-full px-2.5 sm:px-3 py-1 sm:py-1.5 border transition-all duration-200 ${
        active
          ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.15)]"
          : "border-white/10 text-slate-300 hover:text-slate-100 hover:border-white/25"
      }`}
    >
      {children}
    </button>
  );
}

const pillRowCls = "flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible";

export default function Leaderboard() {
  const [params, setParams] = useSearchParams();
  const { regionSlug } = useParams();
  const navigate = useNavigate();
  const category = params.get("category") || "";
  const pathRegion = regionSlug ? REGION_SLUGS[regionSlug.toLowerCase()] || "" : "";
  const region = pathRegion || params.get("region") || "";
  const regionLabel = region ? REGION_LABELS[region] : null;
  const search = params.get("search") || "";
  const sort = params.get("sort") || "rank";
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (region) q.set("region", region);
    if (search) q.set("search", search);
    if (sort !== "rank") q.set("sort", sort);
    api
      .get(`/companies?${q.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [category, region, search, sort]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const goRegion = (r) => {
    const next = new URLSearchParams(params);
    next.delete("region");
    const qs = next.toString();
    const path = r ? `/leaderboard/${r.toLowerCase()}` : "/leaderboard";
    navigate(qs ? `${path}?${qs}` : path);
  };

  return (
    <main data-testid="leaderboard-page" className="pt-28 md:pt-16 min-h-screen">
      <Seo
        title={regionLabel ? `${regionLabel} Startup & Business Leaderboard — HighRank` : "Leaderboard — HighRank"}
        siteName="HighRank"
        description={
          regionLabel
            ? `Discover top-ranked startups and businesses in ${regionLabel} on HighRank. Rank Position is set by Boost, HighRank Score is earned by real reviews — always separate.`
            : "The public leaderboard for startups and businesses. Filter by category and region — Rank Position and HighRank Score shown side by side, always separate."
        }
      />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <FadeUp>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-emerald-400 mb-2">{"// Live rankings"}</p>
            <h1 data-testid="leaderboard-heading" className="font-display text-3xl sm:text-5xl lg:text-6xl uppercase tracking-wide text-slate-100">
              {regionLabel ? `The ${regionLabel} Leaderboard` : "The Leaderboard"}
            </h1>
            <p className="mt-2 max-w-xl text-xs sm:text-sm text-slate-400 leading-relaxed">
              <span className="text-amber-400 font-semibold">Rank Position</span> is set by Boost.{" "}
              <span className="text-emerald-400 font-semibold">HighRank Score</span> is earned by real reviews. Never blended.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex flex-col gap-3 mb-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              update("search", searchInput.trim());
            }}
            className="flex max-w-sm items-center gap-2 rounded-full border border-white/15 bg-[#0A0A09] p-1 pl-3 focus-within:border-emerald-500/60 transition-all duration-300"
          >
            <Search size={14} className="text-slate-400 shrink-0" />
            <input
              data-testid="leaderboard-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search the board…"
              className="flex-1 min-w-0 bg-transparent outline-none text-xs text-slate-100 placeholder:text-slate-600 font-mono2"
            />
            <button
              type="submit"
              data-testid="leaderboard-search-button"
              className="shrink-0 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-[10px] rounded-full px-3 py-1.5 transition-colors duration-200"
            >
              Go
            </button>
          </form>


          <div>
            <p className="font-mono2 text-[9px] uppercase tracking-[0.3em] text-slate-400 mb-1.5">Category</p>
            <div className={pillRowCls} data-testid="leaderboard-category-filters">
              <Pill active={!category} onClick={() => update("category", "")} testId="filter-category-all">
                All
              </Pill>
              {CATEGORIES.map((c) => (
                <Pill
                  key={c}
                  active={category === c}
                  onClick={() => update("category", c)}
                  testId={`filter-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </Pill>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono2 text-[9px] uppercase tracking-[0.3em] text-slate-400 mb-1.5">Region</p>
            <div className={pillRowCls} data-testid="leaderboard-region-filters">
              <Pill active={!region} onClick={() => goRegion("")} testId="filter-region-all">
                All regions
              </Pill>
              {REGIONS.map((r) => (
                <Pill
                  key={r}
                  active={region === r}
                  onClick={() => goRegion(r)}
                  testId={`filter-region-${r.toLowerCase()}`}
                >
                  {r}
                </Pill>
              ))}
            </div>
          </div>
        </div>

        <p data-testid="leaderboard-count" className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-4">
          {loading ? "Loading…" : `${data?.total ?? 0} compan${data?.total === 1 ? "y" : "ies"} on the board`}
        </p>

        <div className="space-y-2.5 pb-14" data-testid="leaderboard-list">
          {!loading && data?.items?.length === 0 && (
            <div className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-8 sm:p-12 text-center">
              <p className="font-heading text-lg font-semibold text-slate-200 mb-2">Nothing here yet.</p>
              <p className="text-sm text-slate-400 mb-6">No companies match this filter. Be the first on the board.</p>
              <Link
                to="/add-business"
                data-testid="leaderboard-empty-cta"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm rounded-full px-6 py-2.5 transition-colors duration-200"
              >
                List your business
              </Link>
            </div>
          )}
          {data?.items?.map((c, i) => (
            <LeaderboardRow key={c.id} company={c} index={i} showWeekly={sort === "views"} />
          ))}
        </div>

        {/* Discover callout */}
        {!loading && (
          <FadeUp>
            <div
              data-testid="leaderboard-discover-callout"
              className="mt-4 mb-14 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] px-5 py-4"
            >
              <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                <Compass size={18} className="text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-slate-100 text-sm">
                  Looking for something more visual?
                </p>
                <p className="text-[12px] text-slate-400 mt-0.5 leading-relaxed">
                  The <span className="text-emerald-400 font-semibold">Discover</span> tab features hand-picked businesses with inline video previews — updated weekly.
                </p>
              </div>
              <Link
                to="/discover"
                data-testid="leaderboard-discover-link"
                className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm rounded-full px-4 py-2 transition-all duration-200"
              >
                Explore Discover <ArrowRight size={13} />
              </Link>
            </div>
          </FadeUp>
        )}
      </section>
    </main>
  );
}
